import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import libraryService from '../../services/libraryService';
import { UploadSimple, Image as ImageIcon } from '@phosphor-icons/react';

const MATERIAL_TYPES = [
  { value: 'book', label: 'Book' },
  { value: 'textbook', label: 'Textbook' },
  { value: 'academic_material', label: 'Academic Material' },
  { value: 'educational_video', label: 'Educational Video' },
  { value: 'worksheet', label: 'Worksheet' },
  { value: 'reference', label: 'Reference' },
];

const CONTENT_TYPES = [
  { value: 'pdf', label: 'PDF' },
  { value: 'video', label: 'Video' },
  { value: 'document', label: 'Document' },
  { value: 'audio', label: 'Audio' },
  { value: 'link', label: 'Link' },
];

const emptyForm = {
  title: '',
  description: '',
  material_type: 'book',
  content_type: 'pdf',
  category_id: '',
  subject_id: '',
  author: '',
  publisher: '',
  edition: '',
  language: '',
  isbn: '',
  publication_year: '',
  external_url: '',
  is_published: true,
  is_featured: false,
};

function LibraryItemForm({ isOpen, onClose, onSaved, categories = [], subjects = [], classes = [], editingItem = null }) {
  const [form, setForm] = useState(emptyForm);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [tagsText, setTagsText] = useState('');
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingItem) {
      setForm({
        title: editingItem.title || '',
        description: editingItem.description || '',
        material_type: editingItem.material_type || 'book',
        content_type: editingItem.content_type || 'pdf',
        category_id: editingItem.category_id || '',
        subject_id: editingItem.subject_id || '',
        author: editingItem.author || '',
        publisher: editingItem.publisher || '',
        edition: editingItem.edition || '',
        language: editingItem.language || '',
        isbn: editingItem.isbn || '',
        publication_year: editingItem.publication_year || '',
        external_url: editingItem.external_url || '',
        is_published: editingItem.is_published !== false,
        is_featured: Boolean(editingItem.is_featured),
      });
      setSelectedClassIds(Array.isArray(editingItem.class_ids) ? editingItem.class_ids : []);
      setTagsText(Array.isArray(editingItem.tags) ? editingItem.tags.join(', ') : '');
    } else {
      setForm(emptyForm);
      setSelectedClassIds([]);
      setTagsText('');
    }
    setFile(null);
    setThumbnail(null);
  }, [editingItem, isOpen]);

  if (!isOpen) return null;

  const toggleClass = (classId) => {
    setSelectedClassIds((current) =>
      current.includes(classId) ? current.filter((id) => id !== classId) : [...current, classId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) return;
        payload.append(key, typeof value === 'boolean' ? String(value) : value);
      });

      const tagNames = tagsText.split(',').map((t) => t.trim()).filter(Boolean);
      tagNames.forEach((tag) => payload.append('tags', tag));
      selectedClassIds.forEach((classId) => payload.append('class_ids', classId));

      if (editingItem) {
        payload.append('tags_provided', 'true');
        payload.append('class_ids_provided', 'true');
      }

      if (file) payload.append('file', file);
      if (thumbnail) payload.append('thumbnail', thumbnail);

      if (editingItem) {
        await libraryService.updateItem(editingItem.id, payload);
        toast.success('Resource updated');
      } else {
        await libraryService.createItem(payload);
        toast.success('Resource created');
      }
      onSaved();
    } catch (error) {
      toast.error(error.message || 'Unable to save resource');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingItem ? 'Edit Resource' : 'Add Resource'}</h2>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close modal">×</button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <input className="form-control" placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <select className="form-control" value={form.material_type} onChange={(e) => setForm({ ...form, material_type: e.target.value })}>
                {MATERIAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <select className="form-control" value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })}>
                {CONTENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <select className="form-control" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.parent_id ? `— ${cat.name}` : cat.name}</option>
                ))}
              </select>
            </div>

            <textarea className="form-control" rows="3" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <input className="form-control" placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
              <input className="form-control" placeholder="Publisher" value={form.publisher} onChange={(e) => setForm({ ...form, publisher: e.target.value })} />
              <input className="form-control" placeholder="Edition" value={form.edition} onChange={(e) => setForm({ ...form, edition: e.target.value })} />
              <input className="form-control" placeholder="Language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} />
              <input className="form-control" placeholder="ISBN (optional)" value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })} />
              <input className="form-control" type="number" placeholder="Publication year" value={form.publication_year} onChange={(e) => setForm({ ...form, publication_year: e.target.value })} />
              <select className="form-control" value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}>
                <option value="">Select subject</option>
                {subjects.map((subj) => <option key={subj.id} value={subj.id}>{subj.name}</option>)}
              </select>
              <input className="form-control" placeholder="External URL" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} />
            </div>

            <input className="form-control" placeholder="Tags (comma separated)" value={tagsText} onChange={(e) => setTagsText(e.target.value)} />

            {classes.length > 0 && (
              <div>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>Visible to classes (leave empty for all classes)</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {classes.map((cls) => (
                    <label key={cls.id} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 10px' }}>
                      <input type="checkbox" checked={selectedClassIds.includes(cls.id)} onChange={() => toggleClass(cls.id)} />
                      {cls.name}{cls.section ? ` ${cls.section}` : ''}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <label className="form-control" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <UploadSimple size={16} />
              <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              {editingItem?.file_url && !file && <span style={{ fontSize: 12, color: '#6b7280' }}>Leave empty to keep existing file</span>}
            </label>

            <label className="form-control" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ImageIcon size={16} />
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setThumbnail(e.target.files?.[0] || null)} />
              {editingItem?.thumbnail_url && !thumbnail && <span style={{ fontSize: 12, color: '#6b7280' }}>Leave empty to keep existing thumbnail</span>}
            </label>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
                Published
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} />
                Featured
              </label>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" type="button" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Resource'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LibraryItemForm;
